import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, ArrowRight, Heart } from 'lucide-react';
import { getProxiedImageUrl } from '../utils/imageProxy';
import { useWishlist } from '../context/WishlistContext';

export default function BusinessCard({ business, size = 'normal', showWishlist = true }) {
  const [heartAnim, setHeartAnim] = useState(false);
  const { toggle, isInWishlist } = useWishlist();

  if (!business) return null;
  const img  = business.immagini?.[0] ? getProxiedImageUrl(business.immagini[0]) : null;
  const logo = business.logo ? getProxiedImageUrl(business.logo) : null;
  const saved = isInWishlist(business._id);

  const location = (() => {
    const { citta, provincia, provincia_codice } = business;
    if (citta && provincia && citta !== provincia) return `${citta} (${provincia_codice || provincia})`;
    if (citta) return citta;
    if (provincia) return provincia;
    return '';
  })();

  const handleHeart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggle(business);
    setHeartAnim(true);
    setTimeout(() => setHeartAnim(false), 400);
  };

  return (
    <Link
      to={`/attivita/${business.slug}`}
      data-testid="business-card"
      className="block rounded-2xl overflow-hidden border bg-white group business-card-hover relative"
      style={{ borderColor: 'var(--mi-border)' }}
    >
      {/* Image */}
      <div
        data-testid="business-card-image"
        className="relative overflow-hidden"
        style={{
          paddingTop: size === 'large' ? '60%' : '56.25%',
          backgroundColor: 'var(--mi-muted)',
        }}
      >
        {img ? (
          <img
            src={img}
            alt={business.nome}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-107"
            style={{ transition: 'transform 0.5s cubic-bezier(0.2,0.8,0.2,1)' }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.style.display = 'none';
            }}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="font-display text-4xl" style={{ color: 'var(--mi-border)' }}>✦</span>
          </div>
        )}

        {/* Gradient overlay — from bottom */}
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(to top, rgba(18,18,20,0.55) 0%, rgba(18,18,20,0.1) 45%, transparent 70%)' }}
        />

        {/* Category badge */}
        {business.categoria && (
          <span
            data-testid="business-card-category-badge"
            className="absolute top-3 right-3 text-xs font-semibold px-2.5 py-1 rounded-full backdrop-blur-sm"
            style={{
              backgroundColor: 'rgba(200,169,110,0.88)',
              color: '#fff',
              textShadow: '0 1px 2px rgba(0,0,0,0.2)',
            }}
          >
            {business.categoria}
          </span>
        )}

        {/* Featured badge */}
        {business.featured && (
          <span
            className="absolute top-3 left-3 text-xs font-medium px-2.5 py-1 rounded-full"
            style={{ backgroundColor: 'var(--mi-fg)', color: '#fff' }}
          >
            In evidenza
          </span>
        )}

        {/* Logo */}
        {logo && (
          <div
            data-testid="business-card-logo"
            className="absolute bottom-3 left-3 w-10 h-10 rounded-full border-2 overflow-hidden bg-white shadow-md"
            style={{ borderColor: 'rgba(255,255,255,0.9)' }}
          >
            <img
              src={logo}
              alt={`Logo ${business.nome}`}
              className="w-full h-full object-contain p-0.5"
              crossOrigin="anonymous"
              onError={(e) => (e.target.style.display = 'none')}
            />
          </div>
        )}

        {/* Wishlist heart */}
        {showWishlist && (
          <button
            onClick={handleHeart}
            className={`absolute bottom-3 right-3 w-8 h-8 rounded-full flex items-center justify-center backdrop-blur-sm transition-all ${heartAnim ? 'animate-heart-pop' : ''}`}
            style={{
              backgroundColor: saved ? 'rgba(200,169,110,0.95)' : 'rgba(255,255,255,0.85)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }}
            title={saved ? 'Rimuovi dai preferiti' : 'Aggiungi ai preferiti'}
          >
            <Heart
              size={14}
              style={{ color: saved ? '#fff' : 'var(--mi-text)' }}
              fill={saved ? '#fff' : 'none'}
            />
          </button>
        )}

        {/* Payoff slide-up on hover */}
        {business.payoff && (
          <div
            className="absolute bottom-0 left-0 right-0 px-4 pb-3 pt-8 transition-all duration-300"
            style={{
              background: 'linear-gradient(to top, rgba(18,18,20,0.75) 0%, transparent 100%)',
              transform: 'translateY(100%)',
              opacity: 0,
              transition: 'transform 0.3s ease, opacity 0.3s ease',
            }}
            data-payoff
          >
            <p className="text-xs text-white leading-snug line-clamp-2">{business.payoff}</p>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="p-4">
        <h3
          data-testid="business-card-title"
          className="font-display text-lg leading-tight tracking-tight"
          style={{ color: 'var(--mi-fg)' }}
        >
          {business.nome}
        </h3>
        {location && (
          <div
            data-testid="business-card-location"
            className="flex items-center gap-1 mt-1"
            style={{ color: 'var(--mi-text)' }}
          >
            <MapPin size={11} />
            <span className="text-xs">{location}</span>
          </div>
        )}
        {business.descrizione && (
          <p className="mt-2 text-sm leading-relaxed line-clamp-2" style={{ color: 'var(--mi-text)' }}>
            {business.descrizione}
          </p>
        )}
        <div
          data-testid="business-card-cta"
          className="mt-3 flex items-center gap-1 text-xs font-semibold transition-all group-hover:gap-2"
          style={{ color: 'var(--mi-accent)' }}
        >
          Scopri <ArrowRight size={12} />
        </div>
      </div>

      <style>{`
        [data-testid="business-card"]:hover [data-payoff] {
          transform: translateY(0) !important;
          opacity: 1 !important;
        }
        .group-hover\\:scale-107:hover {
          transform: scale(1.07);
        }
      `}</style>
    </Link>
  );
}
